import { Dialog } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { AclsRepository } from '@foundation/acls/state';
import { RequestService } from '@foundation/network/services';
import { NotificationService } from '@foundation/notification';
import { TeamsModals } from '@foundation/teams/modals';
import { TranslationService } from '@foundation/translations/services';
import { map } from 'rxjs';
import { AccessShareModalComponent } from './access-share-modal/access-share-modal.component';

export interface AdminCheckResult {
	isAdmin: boolean;
	isVerified: boolean;
}

@Injectable({ providedIn: 'root' })
export class AccessService {
	private _dialog = inject(Dialog);
	private _notificationService = inject(NotificationService);
	private _teamsModal = inject(TeamsModals);
	private _aclsRepository = inject(AclsRepository);
	private _translationService = inject(TranslationService);
	private _requestService = inject(RequestService);

	private _i18n_readOnly = this._translationService.prep('Read only');
	private _i18n_readWrite = this._translationService.prep('Read and write');
	private _i18n_teamSharing = this._translationService.prep('Team sharing');
	private _i18n_selectAccessLevel = this._translationService.prep('Select access level for the team');
	private _i18n_sharedSuccess = this._translationService.prep('Shared with team successfully.');
	private _i18n_sharedError = this._translationService.prep('Failed to share resource with team: ');

	/**
	 * Open the sharing details modal (Access Control List management)
	 * @param resourceId
	 * @param resourceKind
	 */
	public openSharingDetails(resourceId: string, resourceKind: string, settings: { hideAnonymous?: boolean } = {}) {
		this._dialog.open(AccessShareModalComponent, {
			data: {
				resourceId: resourceId,
				resourceKind: resourceKind,
				hideAnonymous: settings.hideAnonymous ?? false,
			},
		});
	}

	/**
	 * Directly share a resource with one or more teams
	 * @param resourceId
	 * @param resourceKind
	 */
	public shareWithTeam(resourceId: string, resourceKind: string) {
		this._teamsModal
			.openTeamsSelectionDialog({
				selectionConstraints: {
					single: false,
					minTeams: 1,
					maxTeams: 10,
				},
			})
			.closed.subscribe((result) => {
				if (result) {
					this._notificationService
						.selectFromOptions(
							[
								{
									label: this._i18n_readOnly(),
									value: 'read',
								},
								{
									label: this._i18n_readWrite(),
									value: 'write',
								},
							],
							this._i18n_teamSharing(),
							this._i18n_selectAccessLevel(),
							'write'
						)
						.closed.subscribe((optionSelectionResult) => {
							if (!optionSelectionResult) return;
							result.teams.forEach((team) => {
								this._aclsRepository
									.updateAclRights$(resourceId, resourceKind, 'team', team.id, {
										read: 'add',
										write: optionSelectionResult.value === 'write' ? 'add' : 'remove',
									})
									.subscribe({
										next: () => {
											this._notificationService.notify(this._i18n_sharedSuccess());
										},
										error: (error) => {
											this._notificationService.error(this._i18n_sharedError() + error.message);
										},
									});
							});
						});
				}
			});
	}

	public checkAdmin$() {
		return this.checkAdminStatus$().pipe(
			map((response) => {
				return response.isAdmin;
			})
		);
	}

	public checkAdminStatus$() {
		return this._requestService.getBasic$<AdminCheckResult>('/api/access/check_admin').pipe(
			map((response) => {
				return {
					isAdmin: response.result?.isAdmin ?? false,
					isVerified: response.result?.isVerified ?? false,
				};
			})
		);
	}
}
